// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// The decision rules behind the OpenStreetMap import modal, kept out of the course-config
// screen so they can be tested without rendering four thousand lines of form.

import { formatLatLon } from './coordinateInput';

/**
 * How much of a course OpenStreetMap holds.
 *
 * `greens` and `tees` are drawn polygons. `greenPositions` and `teePositions` are what the
 * import would actually produce, which is larger: a hole with nothing drawn still yields a
 * position from its centreline endpoints. Most courses are in exactly that state — Tomelilla GK
 * has 18 centrelines and not one polygon — so reporting only the polygon count told the user a
 * course had no positions when it had eighteen.
 */
export type OsmFeatureCounts = {
  holes: number;
  greens: number;
  tees: number;
  greenPositions?: number;
  teePositions?: number;
  /**
   * Bunker, fairway and water-hazard polygons, as one number.
   *
   * Optional: geodata-service and the app deploy independently, and a server that has not
   * shipped the terrain count yet simply omits it — which must read as "none offered", not as
   * a category stuck at zero for a course that has eighty bunkers drawn.
   */
  terrain?: number;
};

/**
 * Positions are matched to hole centrelines, so a course with no importable holes yields none
 * — however many greens and tees are drawn on it.
 *
 * Without the guard, a course whose holes carry no `ref` number reported "18 green positions"
 * from its polygon count alone, and then imported nothing: every polygon is attributed to a
 * hole through that hole's centreline, and back then a hole with no number had no centreline
 * the importer would read. It does now — such a hole is imported unnumbered, under hole 0 —
 * so the two agree again. The count has to answer "what will this write", not "what is drawn",
 * and `counts.holes` is still the thing that decides whether anything is written at all.
 */
export const greenPositionsOf = (counts: OsmFeatureCounts): number =>
  counts.greenPositions ?? (counts.holes === 0 ? 0 : Math.max(counts.greens, counts.holes));

export const teePositionsOf = (counts: OsmFeatureCounts): number =>
  counts.teePositions ?? (counts.holes === 0 ? 0 : Math.max(counts.tees, counts.holes));

/** The categories the user ticks. */
export type OsmSelection = {
  holes: boolean;
  tees: boolean;
  gps: boolean;
  position: boolean;
  terrain: boolean;
};

/**
 * Whether the candidate has a usable coordinate.
 *
 * Every OSM match carries one — it is how the search ranked them — so this is really a guard
 * against a malformed payload rather than a real absence. Null island counts as absent: it is
 * what an unset field serialises to, and it is in the sea.
 */
export const hasCoordinate = (match: { lat?: number | null; lon?: number | null }): boolean => {
  const { lat, lon } = match;
  if (typeof lat !== 'number' || typeof lon !== 'number') return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return false;
  return Math.abs(lat) > 1e-9 || Math.abs(lon) > 1e-9;
};

/**
 * The two points a match's distance was measured between, formatted for display.
 *
 * The distance alone is unreadable in the result list: "12.4 km" is a course 12 km away when
 * the search ran from the phone, and this same course against a stored position that is 12 km
 * wrong when it ran from the club record. Showing both ends lets the reader tell which.
 *
 * Null when either end is missing — a match with no coordinate has nothing to compare, and
 * there is no anchor until a search has run.
 */
export const matchCoordinatePair = (
  anchor: { lat: number; lon: number } | null | undefined,
  match: { lat?: number | null; lon?: number | null }
): { origin: string; osm: string } | null => {
  if (!anchor || !hasCoordinate(anchor) || !hasCoordinate(match)) return null;
  return {
    origin: formatLatLon({ lat: anchor.lat, lon: anchor.lon }),
    osm: formatLatLon({ lat: match.lat as number, lon: match.lon as number }),
  };
};

/**
 * What the server did to produce a result list.
 *
 * `path` is the one thing the matches themselves cannot say: an empty `locality` hints that the
 * radius fallback answered, but it cannot separate "Nominatim knew nothing" from "Nominatim knew
 * the course but not its address", and `radius-nearest` — a list matched on nothing at all,
 * offered because the alternative was showing nothing — looks identical to a real answer.
 */
export type OsmSearchDiagnostics = {
  path: string;
  anchorLat: number;
  anchorLon: number;
  /** `caller` for the coordinate the client sent, `place` when a named town moved the search. */
  anchorSource: string;
  variantsTried: string[];
  nominatimResults: number;
  radiusCandidates: number;
};

/**
 * Reads a search response in either shape.
 *
 * The endpoint returned a bare array before it carried diagnostics, and geodata-service and the
 * app deploy independently — so both orderings have to work rather than one of them emptying the
 * result list.
 */
export const readSearchResponse = <T,>(
  payload: unknown
): { matches: T[]; diagnostics: OsmSearchDiagnostics | null } => {
  if (Array.isArray(payload)) return { matches: payload as T[], diagnostics: null };
  if (payload && typeof payload === 'object') {
    const envelope = payload as { matches?: unknown; diagnostics?: unknown };
    if (Array.isArray(envelope.matches)) {
      return {
        matches: envelope.matches as T[],
        diagnostics: (envelope.diagnostics as OsmSearchDiagnostics | undefined) ?? null,
      };
    }
  }
  return { matches: [], diagnostics: null };
};

/** Where a search anchor came from — see `pickSearchAnchor`. */
export type OsmSearchAnchorSource = 'club' | 'device' | 'map';

export type OsmSearchAnchor = { lat: number; lon: number; source: OsmSearchAnchorSource };

/**
 * Which coordinate a search should be anchored on, in priority order.
 *
 * A point picked on the map always wins: it is offered specifically because the automatic
 * anchor was wrong, so it is the most deliberate answer available to "where is this course".
 * The club's stored position is the ordinary default, and the device's own position stands in
 * only when neither of the other two is known.
 */
export const pickSearchAnchor = (
  mapAnchor: { lat: number; lon: number } | null | undefined,
  clubCoords: { lat: number; lon: number } | null | undefined,
  deviceCoords: { lat: number; lon: number } | null | undefined
): OsmSearchAnchor | null => {
  if (mapAnchor) return { lat: mapAnchor.lat, lon: mapAnchor.lon, source: 'map' };
  if (clubCoords) return { lat: clubCoords.lat, lon: clubCoords.lon, source: 'club' };
  if (deviceCoords) return { lat: deviceCoords.lat, lon: deviceCoords.lon, source: 'device' };
  return null;
};

/** A ring vertex, named rather than the GeoJSON pair — see OsmRingPoint on the server. */
export type OsmRingPoint = { lat: number; lon: number };

export type OsmImportFeature = {
  holeNumber: number;
  kind: 'tee' | 'green' | 'bunker' | 'water' | 'fairway';
  ordinal: number;
  name: string | null;
  lat: number;
  lon: number;
  /** The traced boundary. Present for terrain kinds, absent for tees and greens. */
  polygon?: OsmRingPoint[] | null;
  /** True when the position is a centreline endpoint rather than a drawn shape. */
  approximate?: boolean;
  /** Which OSM element this came from, so a later refresh can find it again. */
  osmType?: string | null;
  osmId?: number | null;
  osmVersion?: number | null;
};

/** The kinds that arrive as boundaries and are written from the terrain lookup. */
export const TERRAIN_KINDS: readonly OsmImportFeature['kind'][] = ['bunker', 'water', 'fairway'];

const isTerrain = (feature: OsmImportFeature): boolean =>
  (TERRAIN_KINDS as readonly string[]).includes(feature.kind);

/**
 * Whether a category has anything to import.
 *
 * An unavailable category is shown with its zero count and disabled rather than hidden —
 * seeing "0 greens" explains itself, where a missing row just looks broken.
 */
export const isCategoryAvailable = (
  counts: OsmFeatureCounts,
  category: keyof OsmSelection,
  coordinateKnown = false
): boolean => {
  if (category === 'holes') return counts.holes > 0;
  // Tees creates the course's tee records, which needs drawn, named tee boxes. Centreline
  // endpoints give a position but no name and only one per hole, so they cannot stand in here.
  if (category === 'tees') return counts.tees > 0;
  // Position asks nothing of the mapping: a club node with no geometry at all still says
  // where the course is, which is the one thing the imagery analyser needs to start.
  if (category === 'position') return coordinateKnown;
  // Terrain needs both halves: drawn polygons, and numbered holes to attribute them to. A
  // bunker matched to no hole is stored but belongs to no hole's picture, so a course with
  // eighty bunkers and no hole refs would offer eighty features and show none of them.
  if (category === 'terrain') return (counts.terrain ?? 0) > 0 && counts.holes > 0;
  // GPS is positions, and those exist as soon as anything is mapped — a centreline is enough.
  return greenPositionsOf(counts) > 0 || teePositionsOf(counts) > 0;
};

/** Everything the course actually has is ticked to begin with. */
export const defaultSelection = (counts: OsmFeatureCounts, coordinateKnown = false): OsmSelection => ({
  holes: isCategoryAvailable(counts, 'holes'),
  tees: isCategoryAvailable(counts, 'tees'),
  gps: isCategoryAvailable(counts, 'gps'),
  position: isCategoryAvailable(counts, 'position', coordinateKnown),
  terrain: isCategoryAvailable(counts, 'terrain'),
});

/**
 * Whether this candidate is worth opening at all.
 *
 * A course mapped as a bare outline used to be a dead end — nothing to take, so the modal
 * refused it. Its coordinate is now worth taking on its own: it is what points imagery
 * analysis at the course, and analysis is how a course with no OSM geometry gets any.
 */
export const hasAnythingToImport = (counts: OsmFeatureCounts, coordinateKnown = false): boolean =>
  counts.holes > 0 ||
  counts.greens > 0 ||
  counts.tees > 0 ||
  (counts.terrain ?? 0) > 0 ||
  coordinateKnown;

/**
 * Whether the positions on offer come only from centreline endpoints.
 *
 * Worth telling the user: they are accurate to a couple of metres, but there is one per hole,
 * so a course with several tee boxes still yields a single tee.
 */
export const positionsAreApproximate = (counts: OsmFeatureCounts): boolean =>
  counts.greens === 0 && counts.tees === 0 && counts.holes > 0;

export const canImport = (selection: OsmSelection): boolean =>
  selection.holes || selection.tees || selection.gps || selection.position || selection.terrain;

/** Whether the geometry lookup is needed at all for this selection. */
export const needsGeometry = (selection: OsmSelection): boolean => selection.tees || selection.gps;

/**
 * Whether the terrain lookup is needed.
 *
 * Its own request, and an expensive one — boundaries rather than centre points — so it is made
 * only for the tick that asks for it. See OverpassQueryBuilder.TerrainInCourse for the numbers.
 */
export const needsTerrain = (selection: OsmSelection): boolean => selection.terrain;

/**
 * Whether a terrain response came back empty because the course has no numbered hole
 * centrelines to attribute boundaries to (server's `no_holes_mapped` warning, see
 * OsmGeometryMatcher.MatchAreas) rather than because nothing is drawn.
 *
 * This is the common case, not an edge case: bunkers, tees and greens are frequently mapped on
 * OSM without a `golf=hole` centreline carrying every hole's `ref`. Without surfacing it, a
 * terrain import that matched nothing looked identical to one that succeeded — the summary line
 * just quietly omitted the category.
 */
export const terrainSkippedNoHoles = (warnings: readonly string[] | undefined): boolean =>
  (warnings ?? []).includes('no_holes_mapped');

/**
 * The positions to write for a given selection.
 *
 * Tee positions belong to both **Tees** and **GPS** by design, so ticking both must not write
 * them twice — the server upserts on (hole, kind, ordinal), and this keeps the payload honest
 * too. Ticking only GPS gives positions without tee rows; ticking only Tees gives tee
 * positions but no greens.
 */
export const featuresToImport = (
  features: readonly OsmImportFeature[],
  selection: OsmSelection
): OsmImportFeature[] =>
  features.filter((feature) => {
    // Terrain comes from its own lookup and answers to its own tick. It is never produced by
    // the geometry leg, so this only ever sees it when the two lists have been concatenated.
    if (isTerrain(feature)) return selection.terrain;
    return feature.kind === 'green' ? selection.gps : selection.gps || selection.tees;
  });
